import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import styles from './transactionDetailsForm.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import SelectDetailed from 'src/components/formik-fields/selectDetailed/SelectDetailed';
import adobeTags from 'src/types/adobe-tags';
import { frequencies } from 'src/services/transaction.service';
import AutoSaveTransaction from 'src/components/auto-save/AutoSaveTransaction';
import { useTransaction, useUpdateTransaction } from 'src/hooks/queries/transactions';
import { useParams } from 'react-router-dom';
import { CategoryModel } from 'src/types/category.model';
import { useCategories } from 'src/hooks/queries/categories';
import CategorySelectDetailed from 'src/components/atomic/atoms/categorySelectDetailed/CategorySelectDetailed';
import { useLocation } from 'react-router';
import RulePromptWrapper, { RulePromptState } from 'src/components/atomic/molecules/rulePrompt/RulePrompt';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXp from 'src/types/one-xp';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { TransactionModel } from 'src/types/transaction.model';
import { useUserAttributes } from 'src/hooks/queries/users';
import { shouldShowRulePromptForTransactionDetails } from 'src/utils/transactions.util';

export default function TransactionsDetailsFormFeatureWrapper(): JSX.Element {
    const { data: transactionsContextualRulesFeatureFlag } = useFeatureFlag(
        oneXp.FeatureFlags.TransactionsContextualRules
    );
    return <TransactionsDetailsFormDataWrapper featureFlagOn={transactionsContextualRulesFeatureFlag} />;
}

interface TransactionsDetailsFormDataWrapperProps {
    featureFlagOn?: boolean;
}

export function TransactionsDetailsFormDataWrapper({
    featureFlagOn
}: TransactionsDetailsFormDataWrapperProps): JSX.Element {
    const params = useParams();
    const { pathname } = useLocation();
    const {
        isFetching: isFetchingTransaction,
        isLoading: isLoadingTransaction,
        data: transaction
    } = useTransaction(Number(params.id));
    const { data: userAttributes } = useUserAttributes();
    const { isMinWidthMedium } = useBreakpoints();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { mutateAsync: updateTransaction } = useUpdateTransaction(Number(params.id));
    const [rulePromptState, setRulePromptState] = useState<RulePromptState>(null);
    const showRulePrompt =
        featureFlagOn &&
        !isFetchingTransaction &&
        shouldShowRulePromptForTransactionDetails(rulePromptState, userAttributes);

    const onCloseCustomSubcategoryModal = async (category: CategoryModel): Promise<void> => {
        await updateTransaction({ ...transaction, groupUid: category.id });
    };

    return (
        <Formik
            initialValues={{
                groupUid: transaction?.groupUid,
                frequency: transaction?.frequency
            }}
            enableReinitialize
            onSubmit={() => {}}
        >
            {() => (
                <TransactionsDetailsForm
                    transaction={transaction}
                    categories={categories}
                    pathname={pathname}
                    onCloseCustomSubcategoryModal={onCloseCustomSubcategoryModal}
                    rulePromptState={rulePromptState}
                    setRulePromptState={setRulePromptState}
                    showRulePrompt={showRulePrompt}
                    isMinWidthMedium={isMinWidthMedium}
                    isLoading={isLoadingTransaction || isLoadingCategories}
                />
            )}
        </Formik>
    );
}

export interface TransactionsDetailsFormProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    pathname: string;
    onCloseCustomSubcategoryModal: (category: CategoryModel) => Promise<void>;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
    showRulePrompt?: boolean;
    isMinWidthMedium?: boolean;
    isLoading?: boolean;
}

export function TransactionsDetailsForm({
    transaction,
    categories,
    pathname,
    onCloseCustomSubcategoryModal,
    rulePromptState,
    setRulePromptState,
    showRulePrompt,
    isMinWidthMedium,
    isLoading
}: TransactionsDetailsFormProps): JSX.Element {
    // Loading state handled in TransactionDetailsView.tsx
    if (isLoading) return null;

    return (
        <Form className={styles.detailsForm}>
            <div className={styles.dropdownGroup}>
                <CategorySelectDetailed
                    id="groupUid"
                    value={transaction}
                    label={LiteralsService.getLiteral('TransactionDetails.category')}
                    tagName={adobeTags.Dropdown.Category}
                    className={styles.detailsItem}
                    categories={categories}
                    pathname={pathname}
                    onCloseCustomSubcategoryModal={onCloseCustomSubcategoryModal}
                />
                {showRulePrompt && !isMinWidthMedium ? (
                    <RulePromptWrapper
                        transaction={transaction}
                        onClose={() => setRulePromptState(null)}
                        state={rulePromptState}
                        setState={setRulePromptState}
                    />
                ) : null}
                <SelectDetailed
                    id="frequency"
                    isForFrequency
                    label={LiteralsService.getLiteral('TransactionDetails.frequency')}
                    options={frequencies}
                    className={styles.detailsItem}
                    frequencyTooltipClassName={styles.frequencyTooltip}
                    tagName={adobeTags.Dropdown.Frequency}
                />
            </div>
            {showRulePrompt && isMinWidthMedium ? (
                <RulePromptWrapper
                    transaction={transaction}
                    onClose={() => setRulePromptState(null)}
                    state={rulePromptState}
                    setState={setRulePromptState}
                />
            ) : null}
            <AutoSaveTransaction
                transaction={transaction}
                debounceMs={300}
                handleCategoryChange={() => setRulePromptState(RulePromptState.STANDARD)}
            />
        </Form>
    );
}
